import { localeToRatingTextMap } from '@src/cnst/star-rating.cnst'
import { FsaBadge } from '@src/components/fsa-badge/FsaBadge.component'
import { HeroRating } from '@src/components/hero-rating/HeroRating.component'
import { SignupButton } from '@src/components/signup-button/SignupButton.component'
import { languageContext } from '@src/context/language.context'
import { useAppSelector } from '@src/hooks/useAppSelector'
import {
  GeolocationCountry,
  selectGeolocationCountry,
} from '@src/store/geolocation/geolocation.slice'
import { DatoCmsFileField } from 'dato-cms-assets'
import { ReactNode, useContext, useEffect, useMemo, useState } from 'react'
import { ClientOnly } from '../client-only/ClientOnly.component'
import { NcImage } from '../nc-image/NcImage.component'
import styles from './InfluencerHero.module.scss'

export interface InfluencerHeroProps {
  heading: string
  subheading: string
  signupText: string
  profileImages: DatoCmsFileField[]
  page: 'Birth Control' | 'Plan Pregnancy' | 'samsung'
}
export interface SummaryCode {
  code: string
}

export function InfluencerHero({
  page,
  heading,
  subheading,
  signupText,
  profileImages,
}: InfluencerHeroProps): ReactNode {
  const { locale } = useContext(languageContext)
  const country = useAppSelector(selectGeolocationCountry)

  const [lcCode, setLcCode] = useState<string | undefined>(undefined)
  const [influencerName, setInfluencerName] = useState<string>('')
  const [influencerPercentage, setInfluencerPercentage] = useState<string>('')

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const discountCode = window.sessionStorage.getItem('discountCode')
      const discountName = window.sessionStorage.getItem('discountName')
      let discountPercentage = window.sessionStorage.getItem('discountPercentage')

      if (discountCode) {
        setLcCode(discountCode.toLowerCase())
      }

      if (discountName) {
        setInfluencerName(discountName)
      }

      if (discountPercentage) {
        if (locale === 'sv-SE') {
          discountPercentage = discountPercentage.replace('OFF', 'RABATT')
        }
        setInfluencerPercentage(discountPercentage)
      }
    }
  }, [locale])

  const reviewText = useMemo(() => {
    return localeToRatingTextMap[locale]
  }, [locale])

  const influencerPhoto = useMemo(() => {
    if (!lcCode) {
      return locale === 'en-US' && country === GeolocationCountry.US ? 1 : 0
    }

    const photo = profileImages.findIndex(image => image.basename.toLowerCase() === lcCode)

    if (photo > 0) {
      return photo
    }

    return locale === 'en-US' && country === GeolocationCountry.US ? 1 : 0
  }, [lcCode, country, locale, profileImages])

  const dataBadgeText = useMemo(() => {
    return locale === 'sv-SE' ? 'Din data är alltid säker' : 'Your data is always protected'
  }, [locale])

  const subheadingWithName = useMemo(() => {
    if (lcCode === 'danifree') {
      /**
       * no need to translate in Swedish, this influencer redirects only to the English site
       */
      return 'Join <span>Dani</span> and try <span>Natural Cycles</span> for free'
    }

    return subheading
      .replace('{influencerName}', influencerName)
      .replace('{influencerPercentage}', influencerPercentage)
  }, [subheading, lcCode, influencerName, influencerPercentage])

  return (
    <>
      <section className={styles.InfluencerHero}>
        <ClientOnly>
          {lcCode && <div className={styles.InfluencerHero__bannerPadding} />}
        </ClientOnly>
        <div className={styles.InfluencerHero__container}>
          <ClientOnly>
            {lcCode && <p className={styles.InfluencerHero__code}>{lcCode}</p>}

            {influencerPhoto !== undefined && profileImages[influencerPhoto] && (
              <NcImage
                className={styles.InfluencerHero__image}
                {...profileImages[influencerPhoto]}
                loading="eager"
              />
            )}
          </ClientOnly>

          <h1 className={styles.InfluencerHero__heading}>{heading}</h1>
          <div
            className={styles.InfluencerHero__text}
            dangerouslySetInnerHTML={{
              __html: subheadingWithName,
            }}
          />
          <div className={styles.InfluencerHero__signup}>
            <SignupButton theme="shimmerGradient"> {signupText} </SignupButton>
          </div>

          <div
            className={`${styles.InfluencerHero__acknowledgement} ${
              locale !== 'en-US' ? styles.InfluencerHero__acknowledgement___centered : ''
            }`}
          >
            {locale === 'en-US' && <FsaBadge theme="light" />}
            <div className={styles.InfluencerHero__rating}>
              <HeroRating
                reviews={reviewText}
                layout={'alignStarsRight'}
                appDistributor={lcCode === 'samsung_nc_20' ? 'Google Play' : 'App Store'}
                textColor="white"
                textAlignLeft={locale === 'en-US'}
                starCustomColor="#FFBD00"
                customScoreSize="medium"
              />
            </div>
          </div>
        </div>
      </section>

      {page !== 'samsung' ? (
        <div
          className={`${styles.InfluencerHero__concaveCurve} ${styles.InfluencerHero__concaveCurve___white}`}
        >
          <svg
            width="375"
            height="12"
            viewBox="0 0 375 12"
            fill="#fff"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M187.5 12C313 12 375 0 375 0H0C0 0 56.5 12 187.5 12Z" fill="#72035d" />
          </svg>
        </div>
      ) : (
        <div className={styles.InfluencerHero__badgeContainer}>
          <div className={styles.InfluencerHero__concaveCurve}>
            <svg
              width="375"
              height="12"
              viewBox="0 0 375 12"
              fill="#fff"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M187.5 12C313 12 375 0 375 0H0C0 0 56.5 12 187.5 12Z" fill="#72035d" />
            </svg>
          </div>

          <div className={styles.InfluencerHero__badge}>
            <div className={styles.InfluencerHero__badgeData}>
              <svg
                width="40"
                height="40"
                viewBox="0 0 40 40"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="20" cy="20" r="20" fill="#E2D4D4" />
                <path
                  d="M26 16H25V14C25 11.24 22.76 9 20 9C17.24 9 15 11.24 15 14V16H14C12.9 16 12 16.9 12 18V28C12 29.1 12.9 30 14 30H26C27.1 30 28 29.1 28 28V18C28 16.9 27.1 16 26 16ZM20 25C18.9 25 18 24.1 18 23C18 21.9 18.9 21 20 21C21.1 21 22 21.9 22 23C22 24.1 21.1 25 20 25ZM23.1 16H16.9V14C16.9 12.29 18.29 10.9 20 10.9C21.71 10.9 23.1 12.29 23.1 14V16Z"
                  fill="#72035D"
                />
              </svg>
              <p className={styles.InfluencerHero__badgeText}> {dataBadgeText} </p>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
